import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  linkedSignal,
  model,
  signal,
  viewChildren,
} from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { ClarityModule } from '@clr/angular';
import { User } from 'src/app/models/api/User';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { SetupConfig } from 'src/app/services/_offline/rep-setup.service';
import { APIService } from 'src/app/services/APIService/api.service';
import { ColumnFilterComponent } from '../../filter/column-filter/column-filter.component';
import { unionBySort } from 'src/app/misc/functions';
import { findTermine } from 'src/app/content/Wartung/wartung.component';
import { WartungApiService } from 'src/app/services/APIService/wartung.api.service';
import { WartungsPlanNode } from '../../_overlays/wartungsverwaltung/wartung.types';
import { map } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { FILTER_TABLE_STORE, provideFilterStore } from 'src/app/shared/stores/filter-table.store';
import { AllSelectedBtnComponent } from '../../_shared/all-selected-btn/all-selected-btn.component';

@Component({
  selector: 'app-replikat-wartungen-setup',
  templateUrl: './replikat-wartungen-setup.component.html',
  styleUrl: './replikat-wartungen-setup.component.scss',
  imports: [
    CommonModule,
    ClarityModule,
    FormsModule,
    ColumnFilterComponent,
    AllSelectedBtnComponent,
    MrTranslatePipe,
    HideIDColumnsPipe,
    TablePrettyPrintPipe,
  ],
  providers: [provideFilterStore()],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReplikatWartungenSetupComponent {
  protected readonly filterStore = inject(FILTER_TABLE_STORE);
  private readonly apiService = inject(APIService);
  private readonly api = inject(WartungApiService);

  readonly modalOpen = model.required();
  readonly setupConfig = model.required<SetupConfig>();

  private readonly columnFilters = viewChildren<ColumnFilterComponent>('wartungenFilter');

  private readonly usersResource = rxResource({
    params: () => this.modalOpen() || undefined,
    stream: () => this.apiService.getUsers(),
  });

  protected readonly users = computed(() => this.usersResource.value() ?? { columns: [], rows: [] });
  protected readonly selectedUser = linkedSignal<User>(() => (this.isForSelf(), undefined));
  protected readonly isForSelf = signal(false);

  private readonly wartungenResource = rxResource({
    params: () => (this.isForSelf() ? { id: 0 } : this.selectedUser()),
    stream: ({ params: { id } }) =>
      this.api.getWartungsPlan('table', id).pipe(map(({ columns, rows }) => ({ columns, rows: findTermine(rows) }))),
  });

  protected readonly isLoading = computed(() => this.usersResource.isLoading() || this.wartungenResource.isLoading());

  protected readonly wartungen = computed(() => this.wartungenResource.value() ?? { columns: [], rows: [] });
  protected readonly selectedWartungen = linkedSignal<WartungsPlanNode[]>(() => (this.selectedUser(), []));
  protected readonly savedSelected = linkedSignal<WartungsPlanNode[]>(() => (this.selectedUser(), []));

  protected replicate() {
    this.setupConfig.set({
      repType: 'Wartung',
      IDs: this.savedSelected().map(x => +x.OSTAMMID),
      userId: this.selectedUser()?.id,
    });
    this.modalOpen.set(false);
  }

  constructor() {
    this.filterStore.registerTable(this.wartungen);
    this.filterStore.registerFilters(this.columnFilters);

    effect(() => {
      const selected = this.selectedWartungen();
      if (selected.length) this.savedSelected.update(saved => unionBySort(selected, saved, 'ID'));
    });
  }

  protected deleteFromSaved(index: number) {
    const row = this.savedSelected()[index];
    this.selectedWartungen.update(selected => {
      const index = selected.indexOf(row);
      if (index != -1) selected.splice(index, 1);
      return [...selected];
    });
    this.savedSelected.update(saved => (saved.splice(index, 1), [...saved]));
  }
}
